import type { BacktestStats } from "@/lib/backtest-stats";

/** The last run's headline numbers, kept on the backtest so the list can show them without running it. */
export interface RunSummary {
  /** Net profit in the quote currency. */
  pnl: number;
  /** Net profit as a percent of the starting money. */
  pnl_pct: number;
  /** 0..1 */
  win_rate: number;
  trades: number;
  /** When the run finished, ISO. */
  at: string;
}

const round = (n: number, digits: number) => (Number.isFinite(n) ? Number(n.toFixed(digits)) : 0);

export function runSummary(stats: BacktestStats, at = new Date().toISOString()): RunSummary {
  return {
    pnl: round(stats.net_profit, 2),
    pnl_pct: round(stats.net_profit_pct, 2),
    win_rate: round(stats.win_rate, 4),
    trades: stats.total_trades,
    at,
  };
}

/** Same numbers, whenever they were run: nothing worth saving again. */
export function sameSummary(a: RunSummary | undefined | null, b: RunSummary): boolean {
  return Boolean(a) && a!.pnl === b.pnl && a!.pnl_pct === b.pnl_pct && a!.win_rate === b.win_rate && a!.trades === b.trades;
}

/** The summary stored on a backtest, if it holds one. */
export function storedSummary(metadata: { ui?: { summary?: unknown } } | null | undefined): RunSummary | null {
  const s = metadata?.ui?.summary as Partial<RunSummary> | undefined;
  if (!s || typeof s.pnl !== "number" || typeof s.trades !== "number") return null;
  return {
    pnl: s.pnl,
    pnl_pct: typeof s.pnl_pct === "number" ? s.pnl_pct : 0,
    win_rate: typeof s.win_rate === "number" ? s.win_rate : 0,
    trades: s.trades,
    at: typeof s.at === "string" ? s.at : "",
  };
}
